/**
 * Pure presenter for the MedChron export appearance/layout field set — visit heading
 * separator, color, underline, date format, group-by, sort field/direction, and collapse
 * therapy visits. Rendered by both the `/profile` Preferences tab and the Customize Export
 * dialog so the two surfaces never fork the control markup.
 */
import { SelectInput } from "@/components/shared/FormInputs";
import { Switch } from "@/components/ui/switch";
import {
  type VisitHeadingSeparatorToken,
  visitHeadingSeparatorFromUiToken,
  visitHeadingSeparatorToUiToken,
} from "@/services/userPreferencesService.mapper";
import type { MedChronExportUiState } from "@/services/userPreferencesService.types";

import {
  DATE_FORMAT_OPTIONS,
  GROUP_BY_OPTIONS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  VISIT_HEADING_COLOR_OPTIONS,
  VISIT_HEADING_SEPARATOR_OPTIONS,
} from "./MedChronExportOptionsFields.constants";

export interface MedChronExportOptionsFieldsProps {
  formState: MedChronExportUiState;
  onFieldChange: (patch: Partial<MedChronExportUiState>) => void;
  /** Distinguishes DOM ids when this field set could ever mount twice in the same tree. */
  idPrefix?: string;
}

export const MedChronExportOptionsFields = ({
  formState,
  onFieldChange,
  idPrefix = "",
}: MedChronExportOptionsFieldsProps) => {
  const selectedSeparatorToken = visitHeadingSeparatorToUiToken(
    formState.visitHeadingDateLocationSeparator
  );

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
      <SelectInput
        id={`${idPrefix}group-visits-by`}
        label="Group By"
        value={formState.chronologyViewGroupBy}
        size="compact"
        options={GROUP_BY_OPTIONS}
        onChange={(value) =>
          onFieldChange({
            chronologyViewGroupBy: value as MedChronExportUiState["chronologyViewGroupBy"],
          })
        }
      />
      <SelectInput
        id={`${idPrefix}date-format`}
        label="Date Format"
        value={formState.dateFormat}
        size="compact"
        options={DATE_FORMAT_OPTIONS.map((option) => ({
          value: option.value,
          label: `${option.label} (${option.sample})`,
        }))}
        onChange={(value) => onFieldChange({ dateFormat: value })}
      />

      <SelectInput
        id={`${idPrefix}sort-field`}
        label="Sort Field"
        value={formState.chronologySortField}
        size="compact"
        options={SORT_FIELD_OPTIONS}
        onChange={(value) =>
          onFieldChange({
            chronologySortField: value as MedChronExportUiState["chronologySortField"],
          })
        }
      />
      <SelectInput
        id={`${idPrefix}sort-direction`}
        label="Sort Direction"
        value={formState.chronologySortDirection}
        size="compact"
        options={SORT_DIRECTION_OPTIONS}
        onChange={(value) =>
          onFieldChange({
            chronologySortDirection: value as MedChronExportUiState["chronologySortDirection"],
          })
        }
      />

      <SelectInput
        id={`${idPrefix}visit-heading-separator`}
        label="Header Separator"
        value={selectedSeparatorToken ?? ""}
        size="compact"
        options={VISIT_HEADING_SEPARATOR_OPTIONS.map((option) => ({
          value: option.token,
          label: option.label,
        }))}
        onChange={(token) =>
          onFieldChange({
            visitHeadingDateLocationSeparator: visitHeadingSeparatorFromUiToken(
              token as VisitHeadingSeparatorToken
            ),
          })
        }
      />
      <SelectInput
        id={`${idPrefix}visit-heading-color`}
        label="Header Color"
        value={formState.visitHeadingTextColor}
        size="compact"
        options={VISIT_HEADING_COLOR_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        onChange={(value) =>
          onFieldChange({
            visitHeadingTextColor: value as MedChronExportUiState["visitHeadingTextColor"],
          })
        }
      />

      <div className="mb-3">
        <label
          htmlFor={`${idPrefix}visit-heading-underline`}
          className="mb-2 block text-sm font-semibold text-muted-foreground"
        >
          Underline Headings
        </label>
        <div className="flex items-center gap-2">
          <Switch
            id={`${idPrefix}visit-heading-underline`}
            checked={formState.visitHeadingUnderline}
            onCheckedChange={(checked) => onFieldChange({ visitHeadingUnderline: checked })}
          />
          <span className="text-sm text-muted-foreground">
            {formState.visitHeadingUnderline ? "Yes" : "No"}
          </span>
        </div>
      </div>
      <div className="mb-3">
        <label
          htmlFor={`${idPrefix}collapse-therapy-visits`}
          className="mb-2 block text-sm font-semibold text-muted-foreground"
        >
          Collapse Therapy Visits
        </label>
        <div className="flex items-center gap-2">
          <Switch
            id={`${idPrefix}collapse-therapy-visits`}
            checked={formState.collapseTherapyVisits}
            onCheckedChange={(checked) => onFieldChange({ collapseTherapyVisits: checked })}
          />
          <span className="text-sm text-muted-foreground">
            {formState.collapseTherapyVisits ? "Yes" : "No"}
          </span>
        </div>
      </div>
    </div>
  );
};
